import { useEffect, useRef, useState } from 'react';
import type { Editor } from 'tinymce';
import UploadHubModalAttach from 'components/UploadHub/UploadHubModalAttach';
import type { AttachedFile } from 'components/UploadHub/UploadHubModalAttach';
import {
  buildEmailAttachmentHtml,
  normalizeEmailAttachments,
} from 'components/Dashboard/Influence/CrmEmails/buildEmailAttachmentHtml';
import { setupAttachmentDragAndDrop } from 'components/Dashboard/Influence/CrmEmails/TinyEditor/attachmentDragAndDrop';

type Bookmark = ReturnType<Editor['selection']['bookmarkManager']['getBookmark']>;

export const attachmentContentStyles =
  '.email-attachment-item, .email-attachment-item *{outline: none !important; cursor: pointer !important;} .mce-content-readonly .email-attachment-remove-cell{display: none;}';

const useEditorAttachments = () => {
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [dropFile, setDropFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const bookmarkRef = useRef<Bookmark | null>(null);
  const dragLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!isAttachModalOpen) setDropFile(null);
  }, [isAttachModalOpen]);

  useEffect(() => () => clearTimeout(dragLeaveTimeoutRef.current), []);

  const insertAttachments = (files: AttachedFile[]) => {
    const editor = editorRef.current;
    if (!editor || !files?.length) return;
    const html = buildEmailAttachmentHtml(files);
    const existingAttachments = editor.getBody().querySelectorAll('.email-attachment-item');
    const lastAttachment = existingAttachments[existingAttachments.length - 1];

    if (lastAttachment) {
      editor.undoManager.transact(() => {
        const trailingParagraph = lastAttachment.nextElementSibling;
        if (trailingParagraph?.tagName === 'P' && trailingParagraph.textContent?.trim() === '') {
          trailingParagraph.remove();
        }
        lastAttachment.insertAdjacentHTML('afterend', html);
      });
      editor.fire('change');
      return;
    }

    editor.selection.select(editor.getBody(), true);
    editor.selection.collapse(false);
    editor.focus();
    if (bookmarkRef.current) editor.selection.bookmarkManager.moveToBookmark(bookmarkRef.current);
    editor.execCommand('mceInsertContent', false, html);
  };

  const setupAttachments = (editor: Editor) => {
    editorRef.current = editor;
    setupAttachmentDragAndDrop(editor);
    editor.on('SetContent', () => normalizeEmailAttachments(editor.getBody()));

    const isFileDropAllowed = (event: DragEvent) =>
      !editor.mode.isReadOnly() && Array.from(event.dataTransfer?.types ?? []).includes('Files');

    editor.on('dragover', (event) => {
      if (!isFileDropAllowed(event)) return;
      event.preventDefault();
      setIsDraggingFile(true);
      clearTimeout(dragLeaveTimeoutRef.current);
      dragLeaveTimeoutRef.current = setTimeout(() => setIsDraggingFile(false), 200);
    });
    editor.on('dragend', () => {
      clearTimeout(dragLeaveTimeoutRef.current);
      setIsDraggingFile(false);
    });
    editor.on('Drop', (event) => {
      if (!isFileDropAllowed(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      clearTimeout(dragLeaveTimeoutRef.current);
      setIsDraggingFile(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        bookmarkRef.current = null;
        setDropFile(file);
        setIsAttachModalOpen(true);
      }
    });

    editor.on('click', (event) => {
      const target = event.target as Element;
      const removeBtn = target.closest?.('.email-attachment-remove');
      if (removeBtn) {
        event.preventDefault();
        const item = removeBtn.closest('.email-attachment-item');
        if (item && !editor.mode.isReadOnly()) {
          editor.undoManager.transact(() => item.remove());
          editor.fire('change');
        }
        return;
      }
      const attachmentItem = target.closest?.('.email-attachment-item');
      if (attachmentItem) {
        event.preventDefault();
        const link = attachmentItem.querySelector<HTMLAnchorElement>('a[href]');
        if (link) window.open(link.href, '_blank', 'noopener,noreferrer');
      }
    });

    editor.ui.registry.addButton('attachmentBtn', {
      text: 'Attach file',
      tooltip: '',
      icon: 'attachment',
      onAction: () => {
        bookmarkRef.current = editor.selection.bookmarkManager.getBookmark();
        setIsAttachModalOpen(true);
      },
    });
  };

  const attachModal = (
    <UploadHubModalAttach
      openState={{ isOpen: isAttachModalOpen, setIsOpen: setIsAttachModalOpen }}
      onAttach={insertAttachments}
      initialFile={dropFile}
    />
  );

  const dropOverlay = isDraggingFile && (
    <div
      className='box-rounded-dashed active-dropzone position-absolute d-flex align-items-center justify-content-center'
      style={{ top: 8, left: 8, right: 8, bottom: 8, zIndex: 5, pointerEvents: 'none' }}
    >
      <p className='mb-0 font-weight-bold'>Drop files here</p>
    </div>
  );

  return { setupAttachments, attachModal, dropOverlay };
};

export default useEditorAttachments;
